import React from "react";
import { ShoppingCart, TruckIcon } from "lucide-react";
import type { CartSummaryProps } from "./types";

const CartSummary: React.FC<CartSummaryProps> = ({
  cartItems,
  itemsTotal,
  itemsDiscount,
  subtotal,
  shippingCost,
  estimatedTotal,
  onCheckout,
  isLoading,
  hasAddress,
}) => {
  const formatCurrency = (amount: number) =>
    `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const hasDiscount = itemsDiscount > 0;
  const isEmpty = cartItems.length === 0;
  const totalQuantity = cartItems.reduce((s, i) => s + i.cart_quantity, 0);
  const amountUntilFreeShipping = 100 - subtotal;
  // Show breakdown only when there's something meaningful to break down
  const showBreakdown = !isEmpty && (hasDiscount || shippingCost > 0);

  return (
    <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border overflow-hidden sticky top-4">
      {/* Header */}
      <div className="bg-primary p-6">
        <h3 className="text-xl font-bold flex items-center gap-2 text-primary-foreground">
          <ShoppingCart className="w-5 h-5" />
          Order Summary
        </h3>
        <p className="text-sm text-primary-foreground/70 mt-1">
          {totalQuantity} {totalQuantity === 1 ? "item" : "items"} in your cart
        </p>
      </div>

      <div className="p-6">
        {/* Item Preview */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {cartItems.slice(0, 3).map((item) => (
            <div key={item.id} className="relative shrink-0">
              <img
                src={item.image || "/api/placeholder/60/60"}
                alt={item.name}
                className="w-16 h-16 object-cover rounded-lg border border-section-border shadow-sm"
              />
            </div>
          ))}
          {cartItems.length > 3 && (
            <div className="w-16 h-16 bg-section-surface rounded-lg border border-section-border flex items-center justify-center text-xs text-section-muted font-bold shadow-sm">
              +{cartItems.length - 3}
            </div>
          )}
        </div>

        {/* Price Breakdown — only when meaningful (discount or shipping exists) */}
        {showBreakdown && (
          <div className="space-y-4 mb-6 pb-6 border-b border-section-border">
            {hasDiscount && (
              <div className="flex justify-between text-sm text-section-muted">
                <span>Items total</span>
                <span className="line-through">{formatCurrency(itemsTotal)}</span>
              </div>
            )}

            {hasDiscount && (
              <div className="flex justify-between text-sm bg-accent-subtle -mx-2 px-2 py-2 rounded-lg border border-accent/20">
                <span className="text-accent-dark font-medium">Discount</span>
                <span className="text-accent-dark font-bold">-{formatCurrency(itemsDiscount)}</span>
              </div>
            )}

            <div className="flex justify-between font-semibold text-section-text">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>

            <div>
              <div className="flex justify-between text-sm text-section-muted">
                <span className="flex items-center gap-1">
                  <TruckIcon className="w-4 h-4" />
                  Shipping
                </span>
                <span className={shippingCost === 0 ? "text-green-600 font-semibold" : ""}>
                  {shippingCost === 0 ? "FREE" : formatCurrency(shippingCost)}
                </span>
              </div>
              {shippingCost > 0 && amountUntilFreeShipping > 0 && (
                <p className="text-xs text-section-muted mt-1 pl-5">
                  Add {formatCurrency(amountUntilFreeShipping)} more for free shipping
                </p>
              )}
            </div>
          </div>
        )}

        {/* Total */}
        <div className="bg-section-surface -mx-6 px-6 py-6 mb-6 border-y border-section-border">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-section-text">Total</span>
            <div className="text-right">
              <div className="text-2xl font-extrabold text-section-text">
                {formatCurrency(isEmpty ? 0 : estimatedTotal)}
              </div>
            </div>
          </div>
        </div>

        {/* Checkout Button */}
        <button
          onClick={onCheckout}
          disabled={isLoading || cartItems.length === 0 || !hasAddress}
          className="
            w-full bg-primary text-primary-foreground
            py-4 rounded-xl font-bold text-lg
            transition-all duration-300 ease-out
            hover:opacity-90 hover:-translate-y-0.5
            shadow-[0_4px_14px_rgba(201,169,110,0.2)] hover:shadow-[0_4px_14px_rgba(201,169,110,0.35)]
            active:scale-[0.98] active:translate-y-0
            disabled:opacity-50 disabled:cursor-not-allowed
            disabled:hover:transform-none
            flex items-center justify-center gap-3
            relative overflow-hidden group
          "
        >
          <span className="relative z-10 flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">
              {isLoading ? "Processing..." : "Proceed to Checkout"}
            </span>
          </span>
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 transition-all duration-700 group-hover:translate-x-full"></div>
        </button>

      </div>
    </div>
  );
};

export default CartSummary;
